import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { Icon } from './Icon.js';

/**
 * Failed saves and actions, said where they cannot be missed (owner, 15 September 2026).
 *
 * An error banner sits where its form put it, which after a scroll is off the screen, so a
 * save that failed looked like a save that did nothing. Every failed mutation lands here
 * instead (app/App.tsx, MutationCache). A page that could not LOAD still says so in the
 * page with ErrorBanner, because there the page itself is what failed.
 */
export interface Toast {
  id: number;
  message: string;
  /** Quoting this to support resolves straight to a trace (doc 05 §3). */
  requestId: string | null;
}

interface ToastState {
  toasts: Toast[];
  push: (message: string, requestId: string | null) => void;
  dismiss: (id: number) => void;
}

/** At most this many at once: a burst of failures is one problem, not a wall of them. */
const MAX = 4;
/** Long enough to read a sentence and copy a request ID; held while pointed at. */
const SHOWN_MS = 8_000;

let nextId = 1;

export const useToasts = create<ToastState>((set) => ({
  toasts: [],
  // The same sentence again replaces the one showing, rather than stacking beside it.
  push: (message, requestId) => set((s) => ({
    toasts: [...s.toasts.filter((t) => t.message !== message), { id: nextId++, message, requestId }]
      .slice(-MAX),
  })),
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** A failure as a toast: the API's own sentence, and the request ID when there is one. */
export function toastError(error: unknown): void {
  const message = error instanceof Error && error.message ? error.message : 'Something went wrong';
  const requestId = typeof error === 'object' && error !== null && 'problem' in error
    ? (error as { problem?: { request_id?: string } | null }).problem?.request_id ?? null
    : null;
  useToasts.getState().push(message, requestId);
}

function ToastItem({ toast }: { toast: Toast }) {
  const dismiss = useToasts((s) => s.dismiss);
  const [held, setHeld] = useState(false);

  useEffect(() => {
    if (held) return undefined;
    const timer = setTimeout(() => dismiss(toast.id), SHOWN_MS);
    return () => clearTimeout(timer);
  }, [held, toast.id, dismiss]);

  return (
    <div className="arc-toast" role="alert"
      onMouseEnter={() => setHeld(true)} onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)} onBlur={() => setHeld(false)}>
      <Icon name="warning" size={14} />
      <div className="arc-toast-body">
        <div className="arc-toast-message">{toast.message}</div>
        {toast.requestId && <div className="arc-toast-id arc-mono">{toast.requestId}</div>}
      </div>
      <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon" aria-label="Dismiss"
        onClick={() => dismiss(toast.id)}>
        <Icon name="close" size={12} />
      </button>
    </div>
  );
}

/** Mounted once, beside the router (app/App.tsx). */
export function Toaster() {
  const toasts = useToasts((s) => s.toasts);
  if (toasts.length === 0) return null;
  return (
    <div className="arc-toasts">
      {toasts.map((t) => <ToastItem key={t.id} toast={t} />)}
    </div>
  );
}
